<template>
  <Page class="game">
    <template #topbar>
      <Topbar/>
    </template>
    <template #contentbar>
      <Contentbar>
        <template #left>
          <BButton
            class="game__btn game__btn-back"
            :to="{ name: 'games' }"
          >
            Zurück
          </BButton>
          <BButton
            v-if="
              game != null
              && game.creator === $store.state.auth.uid
              && game.players.length < game.requiredPlayers
              && game.players.length >= 3
            "
            class="game__btn game__btn-start"
            variant="primary"
            @click="onStartGame"
          >
            Spiel starten
          </BButton>
          <BButton
            v-if="
              rounds != null
              && rounds.length > tabIndex + 1
            "
            class="game__btn"
            variant="primary"
            @click="tabIndex += 1"
          >
            Nächste Runde
          </BButton>
          <BButton
            v-if="isGameFinished && !nextGameAlreadyJoined"
            class="game__btn"
            variant="primary"
            :disabled="isJoiningNextGame"
            @click="onNewGame"
          >
            Noch eine Partie? ({{ nextGameJoinedPlayers }}/{{ game.requiredPlayers }})
          </BButton>
          <BButton
            v-if="nextGame != null && nextGame.hash != null"
            class="game__btn"
            variant="primary"
            @click="$router.push({ name: 'game', params: { game: nextGame.hash } })"
          >
            Nächste Partie
          </BButton>
          <span
            v-if="nextGameAlreadyJoined && nextGame.hash == null"
          >
            warte auf andere Spieler für noch eine Runde ({{ nextGameJoinedPlayers }}/{{ game.requiredPlayers }}) ...
          </span>
        </template>
        <template #right>
          <BButton
            v-if="
              game != null
              && !isGameFinished
              && game.creator === $store.state.auth.uid
            "
            class="game__btn-abort"
            variant="warning"
            @mouseenter="hoverAbort = true"
            @mouseleave="hoverAbort = false"
            @click="onAbortGame"
          >
            {{ abortGameText }}
          </BButton>
        </template>
      </Contentbar>
    </template>
    <template #default>
      <div class="game__wrapper">
        <BTabs
          class="game__content"
          content-class="mt-3"
          v-model="tabIndex"
        >
          <NotStarted
            v-if="game != null && game.players.length < game.requiredPlayers"
            :game="game"
          />
          <BTab
            v-for="(round, index) in rounds"
            :key="`round-${round.player}`"
            :title="`Runde ${resolvePlayerName(round.player)}`"
          >
            <RoundSummary
              v-if="round.finished"
              :round="round"
            />
            <RoundCurrent
              v-else
              :round="round"
              :round-nr="index"
              :game-hash="game.hash"
              :players="game.players"
            />
          </BTab>
        </BTabs>
        <PlayerPoints
          class="game__player-points"
          :game="game"
          :round="currentRound"
        />
        <ModalNewGame
          v-if="newGame"
          v-model="newGame"
          hide-player-count
          @create="onCreateGame"
        />
      </div>
    </template>
  </Page>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

import Topbar from '@/layout/components/Topbar.vue';
import Page from '@/layout/components/Page.vue';
import Contentbar from '@/layout/components/Contentbar.vue';
import resolvePlayerName from '@/shared/mixins/resolvePlayerName';

import ModalNewGame from './components/ModalNewGame.vue';
import NotStarted from './components/NotStarted.vue';
import PlayerPoints from './components/PlayerPoints.vue';
import RoundCurrent from './components/RoundCurrent.vue';
import RoundSummary from './components/RoundSummary.vue';
import utilities from './mixins/utilities';

export default {
  name: 'PageGame',
  components: {
    Contentbar,
    Topbar,
    Page,
    ModalNewGame,
    NotStarted,
    PlayerPoints,
    RoundCurrent,
    RoundSummary,
  },
  mixins: [resolvePlayerName, utilities],
  async created() {
    await this.$store.dispatch('games/subscribe');
    this.$store.dispatch('players/subscribe');
    if (this.rounds != null) {
      this.tabIndex = this.rounds.length - 1;
    }
  },
  data() {
    return {
      hoverAbort: false,
      isPosting: false,
      tabIndex: 0,
      currentTextModels: {},
      currentGuessModels: [],
      newGame: false,
      isJoiningNextGame: false,
      joinedNextGame: false,
    };
  },
  computed: {
    game() {
      if (this.$store.state.games.list == null) {
        return null;
      }
      return this.$store.state.games.list[this.$route.params.game];
    },
    nextGame() {
      if (this.game == null || this.game.next == null) {
        return null;
      }
      return this.game.next;
    },
    rounds() {
      if (
        this.game == null
        || this.game.players.length < this.game.requiredPlayers
      ) {
        return null;
      }
      return this.game.rounds;
    },
    currentRound() {
      if (this.rounds == null) {
        return null;
      }
      const currentRound = this.rounds.slice(-1)[0];
      if (currentRound == null || currentRound.finished) {
        return null;
      }
      return currentRound;
    },
    isAllCurrentGuessesFinished() {
      if (this.currentRound == null) {
        return false;
      }
      return this.currentRound.phrases.every((phrase) => {
        if (
          phrase.playerInputs == null
          || Object.values(phrase.playerInputs).length !== this.game.players.length
        ) {
          return false;
        }
        return Object.keys(phrase.playerInputs)
          .filter(player => player !== this.currentRound.player)
          .every(player => phrase.playerInputs[player].guess != null);
      });
    },
    isGameFinished() {
      if (this.game == null || this.rounds == null) {
        return false;
      }
      if (this.game.aborted) {
        return true;
      }
      return this.rounds.filter(round => round.finished).length === this.game.players.length;
    },
    abortGameText() {
      if (this.hoverAbort) {
        return 'zu betrunken?';
      }
      return 'Spiel beenden';
    },
    nextGameJoinedPlayers() {
      if (this.nextGame == null) {
        return 0;
      }
      return this.nextGame.players.length;
    },
    nextGameAlreadyJoined() {
      if (this.nextGame == null) {
        return false;
      }
      return this.nextGame.players.includes(this.$store.state.auth.uid);
    },
  },
  methods: {
    async onStartGame() {
      this.isPosting = true;
      await this.$store.dispatch('games/start', this.game);
      this.isPosting = false;
    },
    onAbortGame() {
      this.$store.dispatch('games/abort', { game: this.game });
    },
    async onNewGame() {
      if (this.nextGame == null) {
        this.newGame = true;
        return;
      }
      this.isJoiningNextGame = true;
      await this.$store.dispatch('games/nextGameJoin', { game: this.game });
      this.joinedNextGame = true;
      if (this.nextGame.players.length === Number(this.game.requiredPlayers)) {
        const newGame = {
          name: this.nextGame.name,
          creator: this.game.creator,
          requiredPlayers: this.game.requiredPlayers,
          hash: uuidv4(),
          players: this.nextGame.players,
          rounds: [],
          previous: this.game.hash,
        };
        await this.$store.dispatch('games/nextGameCreate', {
          ...newGame,
          rounds: [this.getNewRoundForGame(newGame)],
        });
      }
      this.isJoiningNextGame = false;
    },
    async onCreateGame({ name }) {
      const nextGame = {
        name,
        players: [this.$store.state.auth.uid],
      };
      await this.$store.dispatch('games/nextGamePrepare', {
        game: this.game,
        nextGame,
      });
      this.joinedNextGame = true;
      this.newGame = false;
    },
  },
  watch: {
    currentRound() {
      if (
        this.game == null
        || this.$store.state.auth.uid !== this.game.creator
        || !this.isAllCurrentGuessesFinished
      ) {
        return;
      }
      this.$store.dispatch('games/nextRound', {
        game: this.game,
        nextRound: this.getNewRoundForGame(this.game),
      });
    },
    nextGame() {
      if (!this.joinedNextGame || this.nextGame == null || this.nextGame.hash == null) {
        return;
      }
      this.$router.push({ name: 'game', params: { game: this.nextGame.hash } });
    },
  },
};
</script>

<style scoped>
.game__wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
}

.game__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 15px 10px;
}

.game__player-points {
  margin: 15px 10px;
}

.game__btn {
  margin-right: 10px;
}

.game__btn-back,
.game__btn-abort {
  margin: 15px 10px;
}

@media only screen and (max-width: 800px) {
  .game__wrapper {
    display: block;
  }
}
</style>
